export type FieldLimitValidation = {
  length: number;
  maxLength: number;
  remaining: number;
  isNearLimit: boolean;
  isOverLimit: boolean;
  warningText: string;
  errorText: string;
};

export type CardContentValidationResult = {
  canSubmit: boolean;
  missingRequiredContent: boolean;
  submitTooltipText: string;
  title: FieldLimitValidation;
  description: FieldLimitValidation;
};

type ValidateCardContentInput = {
  title?: string | null;
  description?: string | null;
  isCard: boolean;
  titleMaxLength?: number;
  descriptionMaxLength?: number;
  nearLimitRatio?: number;
};

const defaultTitleMaxLength = 200;
const defaultDescriptionMaxLength = 5000;
const defaultNearLimitRatio = 0.9;

const toLimitValidation = (
  value: string | null,
  maxLength: number,
  nearLimitRatio: number,
  fieldName: string,
): FieldLimitValidation => {
  const length = value ? value.length : 0;
  const remaining = maxLength - length;
  const nearLimitThreshold = Math.floor(maxLength * nearLimitRatio);
  const isOverLimit = length > maxLength;
  const isNearLimit = !isOverLimit && length >= nearLimitThreshold;

  const warningText = isNearLimit
    ? `Осталось ${remaining} симв. до лимита (${maxLength}).`
    : "";

  const errorText = isOverLimit
    ? `${fieldName}: превышение на ${Math.abs(remaining)} симв. (максимум ${maxLength}).`
    : "";

  return {
    length,
    maxLength,
    remaining,
    isNearLimit,
    isOverLimit,
    warningText,
    errorText,
  };
};

// Мягкая проверка для первого автосохранения — хватает одного заполненного
// поля независимо от типа, чтобы backend принял full_clean().
export const hasMinimalAutosaveContent = ({
  title,
  description,
}: {
  title?: string | null;
  description?: string | null;
}): boolean => {
  const hasTitle = !!title && title.trim().length > 0;
  const hasDescription = !!description && description.trim().length > 0;

  return hasTitle || hasDescription;
};

export const validateCardContent = ({
  title,
  description,
  isCard,
  titleMaxLength = defaultTitleMaxLength,
  descriptionMaxLength = defaultDescriptionMaxLength,
  nearLimitRatio = defaultNearLimitRatio,
}: ValidateCardContentInput): CardContentValidationResult => {
  const normalizedTitle = title ? title.trim() : "";
  const normalizedDescription = description ? description.trim() : "";

  const hasTitle = normalizedTitle.length > 0;
  const hasDescription = normalizedDescription.length > 0;

  const missingRequiredContent = isCard
    ? !(hasTitle || hasDescription)
    : !(hasTitle && hasDescription);

  const titleValidation = toLimitValidation(
    title,
    titleMaxLength,
    nearLimitRatio,
    "Заголовок",
  );

  const descriptionValidation = toLimitValidation(
    description,
    descriptionMaxLength,
    nearLimitRatio,
    "Текст",
  );

  const tooLongText = "Сократите заголовок или текст.";
  const missingText = isCard
    ? "Заполните заголовок или текст."
    : "Заполните заголовок и текст.";

  const hasTooLongContent =
    titleValidation.isOverLimit || descriptionValidation.isOverLimit;
  const submitTooltipText =
    (missingRequiredContent ? missingText : "") || tooLongText;

  return {
    canSubmit: !missingRequiredContent && !hasTooLongContent,
    missingRequiredContent,
    submitTooltipText,
    title: titleValidation,
    description: descriptionValidation,
  };
};
